'use client';

import * as React from 'react';
import { JacquardProvider, type JacquardProviderProps } from './JacquardProvider';
import { JacquardI18nProvider, type JacquardI18nProviderProps } from './JacquardI18nProvider';
import { JacquardIconProvider, type JacquardIconProviderProps } from './JacquardIconProvider';

/* JacquardRoot — locked provider-stack shorthand.
   Audit #20 (2026-05-16): the kit ships three providers that MUST nest
   in a specific order per ADR-025 §8:
     <JacquardProvider>          (outermost; brand/tokens/base)
       <JacquardI18nProvider>    (middle; locale + direction)
         <JacquardIconProvider>  (innermost; icon overrides)
   Wiring all three by hand at every app root is error-prone, and the
   wrong order silently breaks downstream consumers (e.g., the icon
   provider can't resolve RTL flip without locale context). This wrapper
   composes the locked stack in one call with prop-namespaced inputs. */

export interface JacquardRootProps {
  /** Children rendered inside the locked provider stack. */
  children: React.ReactNode;
  /** Forwarded to the outermost JacquardProvider. */
  provider?: Omit<JacquardProviderProps, 'children'>;
  /** Forwarded to the middle JacquardI18nProvider. */
  i18n?: Omit<JacquardI18nProviderProps, 'children'>;
  /** Forwarded to the innermost JacquardIconProvider. */
  icon?: Omit<JacquardIconProviderProps, 'children'>;
}

export function JacquardRoot({ children, provider, i18n, icon }: JacquardRootProps) {
  /* Each provider gets its scoped sub-prop bag. When a section is
     undefined, the underlying provider gets its default behaviour
     (e.g., system theme, default locale, no icon overrides). The icon
     section synthesizes `iconOverrides: {}` when absent because
     JacquardIconProvider requires the prop. */
  const i18nProps = i18n ?? {};
  const iconProps = icon ?? { iconOverrides: {} };
  const providerProps = provider ?? {};

  return (
    <JacquardProvider {...providerProps}>
      <JacquardI18nProvider {...i18nProps}>
        <JacquardIconProvider {...iconProps}>{children}</JacquardIconProvider>
      </JacquardI18nProvider>
    </JacquardProvider>
  );
}
